import React from 'react';
import { motion } from 'framer-motion';
export interface MapScaleProps {
  /** The scale ratio, e.g. 10000 means 1:10.000 */
  scaleRatio: number;
  /** Length on the map in cm */
  mapLength?: number;
  /** Real-world length (calculated or provided) */
  realLength?: number;
  /** Unit for real-world length */
  unit?: 'cm' | 'm' | 'km';
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function formatNL(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(n);
}
function formatScale(ratio: number): string {
  return `1 : ${new Intl.NumberFormat('nl-NL').format(ratio)}`;
}
const UNIT_LABELS: Record<string, string> = {
  cm: 'centimeter',
  m: 'meter',
  km: 'kilometer'
};
export function MapScale({
  scaleRatio,
  mapLength,
  realLength,
  unit = 'km',
  label,
  mode = 'support'
}: MapScaleProps) {
  if (!scaleRatio || scaleRatio <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-9560">
        <p className="text-red-600 text-sm font-medium" data-id="element-9561">Ongeldige schaal</p>
      </div>;
  }
  // Calculate real length if map length is provided
  const calculatedReal = mapLength !== undefined ? mapLength * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) : realLength;
  // Calculate map length if real length is provided
  const calculatedMap = realLength !== undefined && mapLength === undefined ? realLength * (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) / scaleRatio : mapLength;
  // Scale bar segments (visual representation)
  const segments = 4;
  const segmentWidths = Array.from({
    length: segments
  }, (_, i) => i + 1);
  // Stimulus mode: compact scale bar
  if (mode === 'stimulus') {
    return <div className="bg-emerald-50 rounded-xl px-5 py-4 border border-emerald-200" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-9562">
        {label && <p className="text-sm font-semibold text-emerald-800 mb-3 text-center" data-id="element-9563">
            {label}
          </p>}
        {/* Scale ratio */}
        <div className="text-center mb-3" data-id="element-9564">
          <span className="inline-flex items-center bg-white rounded-lg px-3 py-1.5 border border-emerald-200 font-mono text-sm font-bold text-emerald-900" data-id="element-9565">
            {formatScale(scaleRatio)}
          </span>
        </div>
        {/* Scale bar */}
        <div className="flex items-end justify-center gap-0" data-id="element-9566">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: i * 0.1,
          duration: 0.3
        }} className={`h-3 w-12 border border-emerald-400 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-9567" />)}
        </div>
        <div className="flex justify-between text-[10px] text-emerald-600 mt-1" style={{
        width: `${segments * 48}px`,
        margin: '0 auto'
      }} data-id="element-9568">
          <span data-id="element-9569">0</span>
          <span data-id="element-9570">{formatNL(segments)} cm op kaart</span>
        </div>
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border-2 border-emerald-200 shadow-sm" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-9571">
      {label && <div className="mb-5 text-center" data-id="element-9572">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-emerald-200 shadow-sm" data-id="element-9573">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-9574" />
            <span className="text-sm font-bold text-emerald-900" data-id="element-9575">{label}</span>
          </div>
        </div>}

      {/* Scale ratio display */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4 text-center" data-id="element-9576">
        <p className="text-xs text-emerald-600 font-medium mb-1" data-id="element-9577">Schaal</p>
        <motion.p initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="text-2xl font-bold font-mono text-emerald-900" data-id="element-9578">
          {formatScale(scaleRatio)}
        </motion.p>
        <p className="text-xs text-slate-500 mt-1" data-id="element-9579">
          1 cm op de kaart = {formatNL(scaleRatio)} cm in het echt
        </p>
      </div>

      {/* Scale bar visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-9580">
        <div className="flex items-end justify-center gap-0 mb-2" data-id="element-9581">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.2 + i * 0.1,
          duration: 0.3
        }} className={`h-4 w-14 border border-emerald-500 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-9582" />)}
        </div>
        <div className="flex justify-between text-[10px] text-emerald-600" style={{
        maxWidth: `${segments * 56}px`,
        margin: '0 auto'
      }} data-id="element-9583">
          <span data-id="element-9584">0</span>
          <span data-id="element-9585">{formatNL(segments)} cm</span>
        </div>
      </div>

      {/* Calculation example */}
      {(calculatedReal !== undefined || calculatedMap !== undefined) && <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-9586">
          <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2" data-id="element-9587">
            Berekening
          </h4>
          {calculatedMap !== undefined && calculatedReal !== undefined && <div className="space-y-1 text-sm text-slate-700" data-id="element-9588">
              <p data-id="element-9589">
                <strong data-id="element-9590">{formatNL(calculatedMap)} cm</strong> op de kaart
              </p>
              <p className="text-emerald-600" data-id="element-9591">× {formatNL(scaleRatio)}</p>
              <div className="border-t border-emerald-200 pt-1" data-id="element-9592">
                <p data-id="element-9593">
                  ={' '}
                  <strong data-id="element-9594">
                    {formatNL(calculatedReal)} {unit}
                  </strong>{' '}
                  in het echt
                </p>
              </div>
            </div>}
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 shadow-sm" data-id="element-9595">
        <div className="flex items-start gap-3" data-id="element-9596">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-9597">
            <span className="text-emerald-600 text-sm" data-id="element-9598">🗺️</span>
          </div>
          <div data-id="element-9599">
            <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-9600">
              Wat betekent de schaal?
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9601">
              Schaal <strong data-id="element-9602">{formatScale(scaleRatio)}</strong> betekent: elke{' '}
              <strong data-id="element-9603">1 cm</strong> op de kaart is in werkelijkheid{' '}
              <strong data-id="element-9604">{formatNL(scaleRatio)} cm</strong>
              {scaleRatio >= 100 && <span data-id="element-9605">
                  {' '}
                  ({formatNL(scaleRatio / 100)} {UNIT_LABELS.m})
                </span>}
              {scaleRatio >= 100000 && <span data-id="element-9606">
                  {' '}
                  = {formatNL(scaleRatio / 100000)} {UNIT_LABELS.km}
                </span>}
              .
            </p>
          </div>
        </div>
      </div>
    </div>;
}